import { useState } from "react";
import { updateRolePermissions } from "../services/roleService";
import {
  RolePermissionsByEntity,
  CasbinPolicyRequest,
  UseUpdateRolePermissionsResult,
} from "../types/permissions";

interface UseUpdateRolePermissionsProps {
  roleId: number;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const useUpdateRolePermissions = ({
  roleId,
  onSuccess,
  onError,
}: UseUpdateRolePermissionsProps): UseUpdateRolePermissionsResult => {
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const updatePermissions = async (permissions: RolePermissionsByEntity) => {
    setIsUpdating(true);
    setError(null);

    try {
      // Convert grouped permissions to flat policy list
      const policies: CasbinPolicyRequest[] = [];

      Object.entries(permissions).forEach(([entity, actions]) => {
        Object.entries(actions).forEach(([action, isSelected]) => {
          if (isSelected) {
            policies.push({
              policy_type: "p",
              user_role_id: roleId,
              entity,
              action,
              is_active: true,
            });
          }
        });
      });

      // Validate that at least one permission is selected
      if (policies.length === 0) {
        throw new Error("At least one permission must be selected");
      }

      await updateRolePermissions({
        roleId,
        policies,
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to update role permissions";
      setError(errorMessage);
      
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    updatePermissions,
    isUpdating,
    error,
  };
};

export default useUpdateRolePermissions;
